import { formatDate, formatDuration } from '../../../../domain';
/**
 * As seis tabelas do relatorio de sono, e como uma noite vira cada uma delas.
 *
 * O cliente mandou o PDF que quer (`demandas/SONO_03_09_2026.pdf`): seis tabelas
 * onde a LINHA e um dia e a COLUNA e um atleta. Este arquivo e a descricao delas -
 * o titulo, o que se le de cada noite e em que faixa de cor aquele valor cai.
 *
 * Fica fora do presenter pelo mesmo motivo do `collaborator-table.ts`: e formatacao
 * e regra de leitura, nao ciclo de vida. E fica em UM lugar porque a tela e o PDF
 * desenham exatamente as mesmas seis tabelas - se cada um montasse a sua, elas
 * divergiriam no primeiro ajuste.
 *
 * ## De onde sai cada valor
 *
 * Uma noite (`/v2/dashboard/sleep-report`) traz `bedtime`, `wakeup`, `total`
 * e os tres estagios JA SOMADOS no banco. As seis tabelas vem de uma consulta so, e
 * nao de seis - e nao de uma por atleta.
 */
/**
 * A ordem das abas e das paginas do PDF.
 *
 * Nao e mais a do PDF do cliente (que punha o total antes do leve): decidido em
 * 14/10/2026, relato 12, que o total vem por ULTIMO, depois das tres fases. A ordem
 * e so de exibicao - o endereco usa `SLEEP_TABLE_CODES`, entao link antigo abre a
 * mesma aba.
 */
export const SLEEP_REPORT_TABLES = ['deitou', 'acordou', 'rem', 'profundo', 'leve', 'total'];
/** O codigo de cada tabela no endereco. Ver `CodeTable` em `domain`. */
export const SLEEP_TABLE_CODES = {
    '0': 'deitou',
    '1': 'acordou',
    '2': 'rem',
    '3': 'profundo',
    '4': 'total',
    '5': 'leve'
};
/**
 * Como a tabela se dispoe: uma NOITE por linha (a do PDF do cliente, e o padrao) ou um
 * ATLETA por linha (B-36, relato 16).
 *
 * Com muitos atletas, um por coluna deixa a tabela larga demais para consultar; um por
 * linha cresce para baixo, que e o sentido em que a tela rola. As faixas e a legenda
 * nao mudam: e a mesma tabela, girada.
 */
export const SLEEP_LAYOUTS = ['noites', 'atletas'];
/** O codigo de cada disposicao no endereco. */
export const SLEEP_LAYOUT_CODES = {
    '0': 'noites',
    '1': 'atletas'
};
/**
 * As faixas vieram do proprio PDF, das tarjas coloridas no topo de cada pagina.
 *
 * ## Por que "1h50 ou mais" e nao "≥ 1h50"
 *
 * O relatorio do cliente escreve `≥`, e a tela desenharia isso sem problema. O PDF
 * nao: as fontes padrao do jsPDF sao as catorze do PDF, e Helvetica NAO TEM o glifo
 * de `≥` (U+2265). O caractere e codificado certo e sai como um simbolo estranho na
 * folha - foi o que apareceu na tabela de REM.
 *
 * Havia tres saidas. Embutir uma fonte com o glifo custa centenas de KB no pacote e
 * uma decisao de licenca; escrever diferente na tela e no PDF quebraria a fonte
 * unica que faz os dois desenharem a mesma coisa. Dizer o operador por extenso
 * resolve nos dois lugares, sem simbolo nenhum fora do Latin-1 - e em portugues le
 * melhor do que o simbolo.
 *
 * Tres tabelas nao tem tarja nenhuma la - acordou, duracao total e sono leve -, e
 * aqui elas tambem nao tem. Inventar uma faixa para elas seria inventar um criterio
 * clinico que ninguem pediu.
 */
const DESCRIPTIONS = {
    deitou: {
        title: 'Hora que deitou',
        legend: [
            { band: 'bom', label: 'Dormir antes de 23:00' },
            { band: 'atencao', label: 'Dormir entre 23:00 e 23:59' },
            { band: 'ruim', label: 'Dormir depois de 00:00' }
        ],
        read: (night) => {
            const start = new Date(night.bedtime);
            if (Number.isNaN(start.getTime())) {
                return undefined;
            }
            const hour = start.getHours();
            /*
             * A madrugada e o caso que importa, e ele inverte a intuicao: deitar as
             * 01:00 e o PIOR resultado, nao o melhor, embora 1 seja um numero
             * pequeno. Por isso a faixa olha a janela do dia, e nao o relogio.
             */
            const band = hour < 23 && hour >= 12 ? 'bom' : hour === 23 ? 'atencao' : 'ruim';
            return { text: clockTime(start), band };
        }
    },
    acordou: {
        title: 'Hora que acordou',
        legend: [],
        read: (night) => {
            const end = new Date(night.wakeup);
            return Number.isNaN(end.getTime()) ? undefined : { text: clockTime(end) };
        }
    },
    rem: {
        title: 'Duração do sono REM',
        phase: 'rem',
        legend: [
            { band: 'bom', label: 'REM 1h50 ou mais' },
            { band: 'atencao', label: 'REM 1h20-1h49' },
            { band: 'ruim', label: 'REM abaixo de 1h20' }
        ],
        read: (night) => stageCell(night.rem, 110 * 60, 80 * 60)
    },
    profundo: {
        title: 'Duração do sono profundo',
        phase: 'profundo',
        legend: [
            { band: 'bom', label: 'Profundo 1h45 ou mais' },
            { band: 'atencao', label: 'Profundo 1h20-1h44' },
            { band: 'ruim', label: 'Profundo abaixo de 1h20' }
        ],
        read: (night) => stageCell(night.deep, 105 * 60, 80 * 60)
    },
    total: {
        title: 'Duração do sono',
        legend: [],
        read: (night) => stageCell(night.total)
    },
    leve: {
        title: 'Duração do sono leve',
        phase: 'leve',
        legend: [],
        read: (night) => stageCell(night.light)
    }
};
/** `hh:mm`, que e como o PDF do cliente escreve as duas horas. */
function clockTime(when) {
    return `${String(when.getHours()).padStart(2, '0')}:${String(when.getMinutes()).padStart(2, '0')}`;
}
/**
 * Classifica uma duracao, quando ha limites.
 *
 * Devolve `undefined` quando o valor e zero: celula VAZIA e o que o PDF mostra, e e
 * mais honesto que um zero - a pulseira pode simplesmente nao ter registrado aquele
 * estagio, o que e diferente de ele nao ter acontecido.
 */
function stageCell(seconds, goodLimit, warnLimit) {
    if (!seconds || seconds <= 0) {
        return undefined;
    }
    if (goodLimit === undefined || warnLimit === undefined) {
        return { text: formatDuration(seconds) };
    }
    const band = seconds >= goodLimit ? 'bom' : seconds >= warnLimit ? 'atencao' : 'ruim';
    return { text: formatDuration(seconds), band };
}
/**
 * O dia daquela noite, pela hora em que a pessoa DEITOU.
 *
 * Deitar 30/08 as 22:42 e dormir ate 31/08 as 09:46 e a noite do dia 30 - e assim
 * que o PDF do cliente as agrupa, e e por isso que a coluna mistura horarios de
 * noite com horarios de madrugada.
 */
export function nightKey(night) {
    return night.bedtime.slice(0, 10);
}
/**
 * Monta as seis tabelas.
 *
 * `nightsByPatient` e o que veio da rede: uma lista de noites por atleta. `days` sao
 * os dias do periodo pedido, ja na ordem em que devem aparecer - quem decide o
 * periodo e o presenter, e vazio nao e erro, e so um relatorio sem linhas.
 */
export function buildSleepReport(columns, days, nightsByPatient) {
    // Uma noite por atleta por dia. Quando a pulseira registra duas, a MAIS LONGA
    // vence: e a noite, e nao o cochilo.
    const byAthleteAndDay = new Map();
    for (const [patientId, nights] of nightsByPatient) {
        const byDay = new Map();
        for (const night of nights) {
            const day = nightKey(night);
            const current = byDay.get(day);
            if (!current || night.total > current.total) {
                byDay.set(day, night);
            }
        }
        byAthleteAndDay.set(patientId, byDay);
    }
    return SLEEP_REPORT_TABLES.map((key) => {
        const description = DESCRIPTIONS[key];
        const rows = days.map((date) => ({
            date,
            label: formatDate(date),
            cells: columns.map((column) => {
                const night = byAthleteAndDay.get(column.patientId)?.get(date);
                const reading = night ? description.read(night) : undefined;
                return reading ?? { text: '' };
            })
        }));
        return {
            key,
            title: description.title,
            phase: description.phase,
            legend: description.legend,
            columns,
            rows
        };
    });
}
/**
 * A fase que uma tabela descreve, sem precisar montar a tabela.
 *
 * A aba existe antes de qualquer dado chegar - as seis sao criadas na inicializacao
 * -, entao ela precisa perguntar a fase ao CATALOGO. Uma tabela paralela de
 * `aba -> fase` seria uma segunda fonte da mesma verdade, e o dia em que as duas
 * discordassem ninguem notaria: a aba mostraria uma cor e a tabela outra.
 */
export function phaseOfTable(key) {
    return DESCRIPTIONS[key].phase;
}
/** Todos os dias de um periodo, inclusive nas duas pontas. */
export function daysBetween(from, to) {
    const days = [];
    const start = new Date(`${from}T12:00:00`);
    const end = new Date(`${to}T12:00:00`);
    for (let day = start; day <= end; day.setDate(day.getDate() + 1)) {
        days.push(day.toISOString().slice(0, 10));
    }
    return days;
}
/**
 * A tabela na disposicao pedida - a MESMA grade para a tela e para o PDF.
 *
 * O dado continua montado com a noite na linha (`buildSleepReport`); girar e so ler a
 * matriz pelo outro lado. Por isso a disposicao nao custa consulta, e as celulas sao
 * as mesmas, com as mesmas faixas.
 */
export function gridOf(table, layout) {
    if (layout === 'noites') {
        return {
            corner: 'Noite',
            headers: table.columns.map((column) => ({
                id: String(column.patientId),
                label: column.name,
                patientId: column.patientId
            })),
            lines: table.rows.map((row) => ({ id: row.date, label: row.label, cells: row.cells }))
        };
    }
    return {
        corner: 'Atleta',
        // `dd/mm`: o ano e o mesmo da tabela inteira, e trinta e cinco datas inteiras
        // no cabecalho fariam a tabela larga de novo - o problema que esta disposicao
        // resolve.
        headers: table.rows.map((row) => ({ id: row.date, label: row.label.slice(0, 5) })),
        lines: table.columns.map((column, index) => ({
            id: String(column.patientId),
            label: column.name,
            patientId: column.patientId,
            cells: table.rows.map((row) => row.cells[index] ?? { text: '' })
        }))
    };
}
//# sourceMappingURL=sleep-report.tables.js.map